/** Shared renderer for the specialist campus rooms. */
import * as pc from 'playcanvas'
import { useArenaStore } from '../../store'
import { INPUT_PRIORITY, modalInputRouter } from '../../input/modalInputRouter'
import { computeAvatarState } from '../avatarState'
import {
  pickVariant,
  spawnSyntyRosterCharacter,
  type CharacterHandle,
} from '../characters'
import type { RosterAgent } from './agentRoster'
import {
  activateCampusRoomAction,
  CAMPUS_ROOMS,
  canUseCampusRoomAction,
  campusRoomActionAt,
  campusRoomView,
  campusRoomWalls,
  datacentreProjectionKey,
  datacentreProjectionLines,
  layoutDatacentreProjection,
  type CampusRoomConfig,
  type CampusRoomId,
  type DatacentreProjectionState,
} from './campusRooms'
import {
  CAFE_AGENT_SLOTS,
  CAFE_COMMON_TABLE,
  CAFE_CONVERSATION_TABLES,
  CAFE_COUNTER_CANOPY,
  CAFE_QUIET_ROOM,
  CAFE_QUIET_SEATING,
  MAX_CAFE_AGENTS,
  cafeProjectionKey,
  cafeProjectionLines,
  layoutCafeProjection,
  type CafeProjectionState,
} from './cafeLayout'
import {
  buildStandardInteriorShell,
  createRenderEntity,
  createWorldOverlay,
  type OverlayHandle,
} from './interiorShared'
import { InteriorDressingKit } from './interiorDressing'
import {
  EXCHANGE_COST_STUDIO,
  EXCHANGE_LIVE_FLOW_INTERACTION,
  MAX_EXCHANGE_RUNS,
  exchangeProjectionKey,
  exchangeProjectionLines,
  layoutExchangeProjection,
  type ExchangeProjectionState,
} from './exchangeLayout'
import {
  MAX_WORKSHOP_PLUGINS,
  WORKSHOP_TEST_CELL,
  WORKSHOP_TEST_TABLE,
  layoutWorkshopProjection,
  workshopProjectionLines,
  workshopProjectionKey,
  type WorkshopProjectionState,
} from './workshopLayout'
import {
  DATACENTRE_CABLE_SPINE_COLLIDER,
  DATACENTRE_NOC_ROOM,
  DATACENTRE_SERVICE_RACKS,
} from './workplaceFurnishing'

function material(color: readonly [number, number, number], emissive = 0): pc.StandardMaterial {
  const value = new pc.StandardMaterial()
  value.diffuse = new pc.Color(...color)
  if (emissive > 0) {
    value.emissive = new pc.Color(...color)
    value.emissiveIntensity = emissive
  }
  value.gloss = 0.5
  value.update()
  return value
}

function textMaterial(
  app: pc.Application,
  title: string,
  subtitle: string,
  accent: string,
): pc.StandardMaterial {
  const value = new pc.StandardMaterial()
  value.diffuse = new pc.Color(0.055, 0.065, 0.085)
  value.emissive = new pc.Color(1, 1, 1)
  value.emissiveIntensity = 0.8
  value.useLighting = false
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 640
    canvas.height = 180
    const context = canvas.getContext('2d')
    if (context) {
      context.fillStyle = '#0e121a'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.fillStyle = accent
      context.fillRect(0, 0, 14, canvas.height)
      context.fillStyle = '#f2f5fa'
      context.font = '700 46px Nunito, system-ui, sans-serif'
      context.fillText(title, 42, 74)
      context.fillStyle = '#9ca9bc'
      context.font = '600 23px ui-monospace, monospace'
      context.fillText(subtitle, 42, 124)
      const texture = new pc.Texture(app.graphicsDevice, { width: canvas.width, height: canvas.height })
      texture.setSource(canvas)
      value.emissiveMap = texture
      value.diffuseMap = texture
    }
  } catch { /* retain a dark physical sign when canvas textures are unavailable */ }
  value.update()
  return value
}

export class CampusRoomSystem {
  private readonly config: CampusRoomConfig
  private app!: pc.Application
  private root!: pc.Entity
  private camera!: pc.Entity
  private overlay: OverlayHandle | null = null
  private status!: HTMLDivElement
  private actionPrompt!: HTMLDivElement
  private indicators!: pc.Entity
  private unsubscribe?: () => void
  private disposeInput?: () => void
  private roster: RosterAgent[] = []
  private active = false
  private currentAction: ReturnType<typeof campusRoomActionAt> = null
  private exchangeStatusEntities: pc.Entity[] = []
  private exchangeStatusMaterials: pc.StandardMaterial[] = []
  private exchangeStatusTextures: Array<pc.Texture | null> = []
  private exchangeRunSignals: pc.Entity[] = []
  private exchangeCapacitySignals: pc.Entity[] = []
  private exchangeLastGood: ExchangeProjectionState | undefined
  private exchangeSemanticKey = ''
  private exchangeMaterials: {
    active: pc.StandardMaterial
    idle: pc.StandardMaterial
    degraded: pc.StandardMaterial
  } | null = null
  private workshopStatusEntity: pc.Entity | null = null
  private workshopStatusMaterial: pc.StandardMaterial | null = null
  private workshopStatusTexture: pc.Texture | null = null
  private workshopBuildSignals: pc.Entity[] = []
  private workshopPluginSlots: pc.Entity[] = []
  private workshopLastGood: WorkshopProjectionState | undefined
  private workshopSemanticKey = ''
  private workshopMaterials: {
    buildActive: pc.StandardMaterial
    buildIdle: pc.StandardMaterial
    pluginHealthy: pc.StandardMaterial
    pluginDegraded: pc.StandardMaterial
    pluginDown: pc.StandardMaterial
    pluginUnknown: pc.StandardMaterial
  } | null = null
  private cafeStatusEntities: pc.Entity[] = []
  private cafeStatusMaterials: pc.StandardMaterial[] = []
  private cafeStatusTextures: Array<pc.Texture | null> = []
  private cafePresenceSignals: pc.Entity[] = []
  private cafeLastGood: CafeProjectionState | undefined
  private cafeSemanticKey = ''
  private cafeAgentKey = ''
  private cafeCharacters = new Map<string, CharacterHandle>()
  private cafeMaterials: {
    active: pc.StandardMaterial
    idle: pc.StandardMaterial
    degraded: pc.StandardMaterial
  } | null = null
  private datacentreStatusEntities: pc.Entity[] = []
  private datacentreStatusMaterials: pc.StandardMaterial[] = []
  private datacentreStatusTextures: Array<pc.Texture | null> = []
  private datacentreServiceSignals: pc.Entity[] = []
  private datacentreWorldCore: pc.Entity | null = null
  private datacentreLastGood: DatacentreProjectionState | undefined
  private datacentreSemanticKey = ''
  private datacentreMaterials: {
    worldReady: pc.StandardMaterial
    worldConnecting: pc.StandardMaterial
    serviceHealthy: pc.StandardMaterial
    serviceDegraded: pc.StandardMaterial
    serviceDown: pc.StandardMaterial
    serviceUnknown: pc.StandardMaterial
    inactive: pc.StandardMaterial
  } | null = null

  constructor(id: CampusRoomId) {
    this.config = CAMPUS_ROOMS[id]
  }

  init(app: pc.Application, root: pc.Entity, camera: pc.Entity): void {
    this.app = app
    this.root = root
    this.camera = camera
    buildStandardInteriorShell({
      root,
      prefix: this.config.id,
      halfX: this.config.halfX,
      halfZ: this.config.halfZ,
      height: this.config.height,
      walls: campusRoomWalls(this.config),
      floorColor: this.config.floorColor,
      wallColor: this.config.wallColor,
      backdropColor: this.config.backdropColor,
      keyLightY: this.config.height - 0.5,
      keyLightRange: Math.max(this.config.halfX, this.config.halfZ) * 3,
      accent: {
        name: 'function-light', color: this.config.accentColor,
        y: 2.3, z: -this.config.halfZ + 3, range: 22,
      },
    })
    this.buildIdentity(app)
    this.buildPurposeFurniture()
    this.indicators = new pc.Entity(`${this.config.id}-indicators`)
    root.addChild(this.indicators)
    if (this.config.id === 'exchange') this.buildExchangeSignals()
    if (this.config.id === 'workshop') this.buildWorkshopPluginSlots()
    if (this.config.id === 'cafe') this.buildCafePresenceSignals()

    this.overlay = createWorldOverlay(app, { display: 'none' })
    this.status = document.createElement('div')
    this.status.dataset.testid = `campus-room-status-${this.config.id}`
    Object.assign(this.status.style, {
      position: 'absolute', top: '4.25rem', left: '1rem', minWidth: '260px',
      padding: '10px 12px', borderRadius: '9px',
      background: 'rgba(8,12,18,.88)', border: '1px solid rgba(148,163,184,.35)',
      color: '#dbe5f5', font: '11px/1.55 ui-monospace, monospace',
      letterSpacing: '.035em', whiteSpace: 'pre-line',
    })
    this.overlay.element.appendChild(this.status)

    this.actionPrompt = document.createElement('div')
    this.actionPrompt.dataset.testid = `campus-room-action-${this.config.id}`
    Object.assign(this.actionPrompt.style, {
      position: 'absolute', left: '50%', bottom: '4.5rem', transform: 'translateX(-50%)',
      display: 'none', padding: '9px 13px', borderRadius: '999px',
      background: 'rgba(7,14,23,.9)', border: '1px solid rgba(125,211,252,.55)',
      color: '#e8f7ff', font: '700 12px/1.2 Nunito, system-ui, sans-serif',
      letterSpacing: '.04em', boxShadow: '0 10px 32px rgba(0,0,0,.35)',
    })
    this.overlay.element.appendChild(this.actionPrompt)

    this.unsubscribe = useArenaStore.subscribe((state, previous) => {
      if (
        state.health !== previous.health || state.runs !== previous.runs ||
        state.agents !== previous.agents || state.chats !== previous.chats ||
        state.interactionsByRun !== previous.interactionsByRun ||
        state.transcriptsByRun !== previous.transcriptsByRun ||
        state.remoteProjectionStatus !== previous.remoteProjectionStatus ||
        state.worldStateStatus !== previous.worldStateStatus
      ) this.sync()
    })
    this.sync()
    this.disposeInput = modalInputRouter.register(INPUT_PRIORITY.room, this.onKeyDown)
  }

  private readonly onKeyDown = (event: KeyboardEvent): boolean => {
    if (!this.active) return false
    const state = useArenaStore.getState()
    return activateCampusRoomAction(
      event.code,
      this.currentAction,
      state,
      document.exitPointerLock?.bind(document),
    )
  }

  private add(
    name: string,
    type: 'box' | 'cylinder' | 'sphere' | 'cone',
    position: readonly [number, number, number],
    scale: readonly [number, number, number],
    mat: pc.Material,
    parent = this.root,
  ): pc.Entity {
    return createRenderEntity({ name, type, position, scale, material: mat, parent })
  }

  private buildIdentity(app: pc.Application): void {
    const accent = material(this.config.accentColor, 2.4)
    const accentHex = this.config.id === 'exchange' ? '#22d3ee'
      : this.config.id === 'workshop' ? '#fb923c'
        : this.config.id === 'cafe' ? '#fb7185' : '#60a5fa'
    const zoneWidth = (this.config.halfX * 2 - 4) / 3
    for (let i = 0; i < 3; i++) {
      const x = -this.config.halfX + 2 + zoneWidth * (i + 0.5)
      const zoneMaterial = textMaterial(
        app,
        this.config.zones[i],
        i === 0 ? this.config.title : this.config.subtitle.split(' · ')[i - 1] ?? this.config.title,
        accentHex,
      )
      const zone = this.add(
        `${this.config.id}-zone-${i}`,
        'box',
        [x, 1.65, -this.config.halfZ + 0.45],
        [zoneWidth - 0.45, 2.3, 0.16],
        zoneMaterial,
      )
      if (this.config.id === 'workshop' && i === 2) {
        this.workshopStatusEntity = zone
        this.workshopStatusMaterial = zoneMaterial
      }
      if (this.config.id === 'exchange') {
        zone.tags.add(
          'exchange-projection-surface',
          `exchange-projection-${['cost', 'flow', 'capacity'][i]}`,
        )
        this.exchangeStatusEntities.push(zone)
        this.exchangeStatusMaterials.push(zoneMaterial)
        this.exchangeStatusTextures.push(zoneMaterial.diffuseMap ?? null)
      }
      if (this.config.id === 'cafe') {
        zone.tags.add(
          'cafe-projection-surface',
          `cafe-projection-${['team', 'chats', 'message'][i]}`,
        )
        this.cafeStatusEntities.push(zone)
        this.cafeStatusMaterials.push(zoneMaterial)
        this.cafeStatusTextures.push(zoneMaterial.diffuseMap ?? null)
      }
      if (this.config.id === 'datacentre') {
        zone.tags.add(
          'datacentre-projection-surface',
          `datacentre-projection-${['world', 'daemon', 'services'][i]}`,
        )
        this.datacentreStatusEntities.push(zone)
        this.datacentreStatusMaterials.push(zoneMaterial)
        this.datacentreStatusTextures.push(zoneMaterial.diffuseMap ?? null)
      }
      this.add(`${this.config.id}-zone-accent-${i}`, 'box', [x, 2.55, -this.config.halfZ + 0.34],
        [zoneWidth - 0.8, 0.07, 0.08], accent)
    }
  }

  private buildPurposeFurniture(): void {
    const dressing = new InteriorDressingKit(this.root)
    const dark = material([0.10, 0.115, 0.14])
    const metal = material([0.19, 0.22, 0.26])
    const accent = material(this.config.accentColor, 1.6)
    const foliage = material([0.16, 0.38, 0.24])
    const soft = material([0.22, 0.19, 0.24])
    const glass = material(
      this.config.id === 'datacentre' ? [0.34, 0.42, 0.50] : [0.24, 0.58, 0.68],
    )
    glass.opacity = this.config.id === 'datacentre' ? 0.14 : 0.22
    glass.blendType = pc.BLEND_NORMAL
    glass.depthWrite = false
    glass.cull = pc.CULLFACE_NONE
    glass.update()

    // Shared architectural rhythm keeps the rooms recognisably part of one
    // campus while each purpose-specific plan below remains distinct.
    if (this.config.id === 'exchange') {
      // A continuous dark lid and three task-light rafts turn the formerly
      // washed-out open shell into one deliberate, zoned workplace.
      this.add(
        'exchange-ceiling-field',
        'box',
        [0, this.config.height - 0.05, 0],
        [this.config.halfX * 2 - 0.4, 0.10, this.config.halfZ * 2 - 0.4],
        material([0.09, 0.11, 0.16], 0.7),
      )
      for (const [id, x, width] of [
        ['cost', -9.5, 6.2],
        ['flow', 0, 6.4],
        ['capacity', 9.5, 6.2],
      ] as const) {
        dressing.addCeilingRaft(
          `exchange-ceiling-${id}`,
          x,
          -4.4,
          width,
          7.2,
          this.config.height,
          dark,
          accent,
        )
      }
      dressing.addPlanter('exchange-entry-planter-west', -3.2, this.config.halfZ - 1.6, metal, foliage)
      dressing.addPlanter('exchange-entry-planter-east', 3.2, this.config.halfZ - 1.6, metal, foliage)
    } else if (this.config.id === 'workshop') {
      // A continuous graphite lid prevents the open shell/sky from washing
      // out the lab. The two lower rafts then read as deliberate task lights.
      this.add(
        'workshop-ceiling-field',
        'box',
        [0, this.config.height - 0.05, 0],
        [this.config.halfX * 2 - 0.4, 0.10, this.config.halfZ * 2 - 0.4],
        material([0.055, 0.06, 0.07]),
      )
      dressing.addCeilingRaft(
        'workshop-ceiling-west',
        -7.5,
        1.2,
        6.2,
        7.2,
        this.config.height,
        dark,
        accent,
      )
      dressing.addCeilingRaft(
        'workshop-ceiling-east',
        7.5,
        1.2,
        6.2,
        7.2,
        this.config.height,
        dark,
        accent,
      )
      this.add(
        'workshop-spine-light',
        'box',
        [0, 0.024, 2.8],
        [2.4, 0.048, 8.8],
        material([0.24, 0.33, 0.35]),
      )
    } else if (this.config.id === 'cafe') {
      this.add(
        'cafe-ceiling-field',
        'box',
        [0, this.config.height - 0.05, 0],
        [this.config.halfX * 2 - 0.4, 0.10, this.config.halfZ * 2 - 0.4],
        material([0.13, 0.085, 0.075]),
      )
      dressing.addCeilingRaft(
        'cafe-ceiling-conversations',
        -7.6,
        0.2,
        8.2,
        9.2,
        this.config.height,
        dark,
        accent,
      )
      dressing.addCeilingRaft(
        'cafe-ceiling-common',
        0,
        -4,
        5.6,
        6.4,
        this.config.height,
        dark,
        accent,
      )
      dressing.addCeilingRaft(
        'cafe-ceiling-quiet',
        CAFE_QUIET_ROOM.centerX,
        CAFE_QUIET_ROOM.centerZ,
        5.4,
        5.6,
        this.config.height,
        dark,
        accent,
      )
      dressing.addPlanter('cafe-entry-planter-west', -3.2, this.config.halfZ - 1.6, metal, foliage)
      dressing.addPlanter('cafe-entry-planter-east', 3.2, this.config.halfZ - 1.6, metal, foliage)
      const pendantGlow = material([1, 0.62, 0.42], 2.2)
      for (const [id, x, z] of [
        ['conversation-west', CAFE_CONVERSATION_TABLES[0].x, CAFE_CONVERSATION_TABLES[0].z],
        ['conversation-north', CAFE_CONVERSATION_TABLES[1].x, CAFE_CONVERSATION_TABLES[1].z],
        ['common', CAFE_COMMON_TABLE.x, CAFE_COMMON_TABLE.z],
        ['quiet', CAFE_QUIET_SEATING.tableX, CAFE_QUIET_SEATING.tableZ],
      ] as const) {
        this.add(`cafe-pendant-${id}-stem`, 'cylinder',
          [x, this.config.height - 0.58, z], [0.035, 0.75, 0.035], metal)
        this.add(`cafe-pendant-${id}-shade`, 'cone',
          [x, this.config.height - 0.98, z], [0.62, 0.36, 0.62], soft)
        this.add(`cafe-pendant-${id}-glow`, 'sphere',
          [x, this.config.height - 1.08, z], [0.18, 0.12, 0.18], pendantGlow)
        const pool = new pc.Entity(`cafe-program-light-${id}`)
        pool.addComponent('light', {
          type: 'spot',
          color: new pc.Color(1, id === 'quiet' ? 0.76 : 0.68, 0.50),
          intensity: id === 'quiet' ? 1.65 : 2.15,
          range: id === 'quiet' ? 5 : 5.8,
          innerConeAngle: 34,
          outerConeAngle: 54,
          castShadows: false,
        })
        pool.setLocalPosition(x, this.config.height - 1.12, z)
        pool.tags.add('cafe-program-light', `cafe-program-light-${id}`)
        this.root.addChild(pool)
      }
      for (const marker of [
        {
          id: 'conversations',
          title: 'CONVERSATIONS',
          subtitle: 'TWO SOCIAL TABLES',
          position: [-8.2, 2.74, 6.25] as const,
          scale: [4.4, 0.46, 0.07] as const,
        },
        {
          id: 'common-table',
          title: 'COMMON TABLE',
          subtitle: 'SHARED PROJECT WORK',
          position: [0, 2.76, -1.0] as const,
          scale: [4.2, 0.46, 0.07] as const,
        },
        {
          id: 'quiet-room',
          title: 'QUIET ROOM',
          subtitle: 'FOCUSED CONVERSATION',
          position: [CAFE_QUIET_ROOM.centerX, 2.94, CAFE_QUIET_ROOM.frontZ + 0.05] as const,
          scale: [3.2, 0.40, 0.07] as const,
        },
      ]) {
        const markerEntity = this.add(
          `cafe-program-wayfinding-${marker.id}`,
          'box',
          marker.position,
          marker.scale,
          textMaterial(this.app, marker.title, marker.subtitle, '#fb7185'),
        )
        markerEntity.tags.add('cafe-program-wayfinding')
      }
    } else if (this.config.id === 'datacentre') {
      this.add(
        'datacentre-ceiling-field',
        'box',
        [0, this.config.height - 0.05, 0],
        [this.config.halfX * 2 - 0.4, 0.10, this.config.halfZ * 2 - 0.4],
        material([0.14, 0.18, 0.24], 0.7),
      )
      for (const [index, z] of [-9, -4.5, 0, 4.5, 9].entries()) {
        this.add(
          `datacentre-ceiling-crossbeam-${index}`,
          'box',
          [0, this.config.height - 0.22, z],
          [this.config.halfX * 2 - 1.1, 0.14, 0.16],
          metal,
        )
      }
      for (const [id, x] of [['west', -8.5], ['east', 5.5]] as const) {
        this.add(
          `datacentre-overhead-tray-${id}`,
          'box',
          [x, this.config.height - 0.44, -0.05],
          [2.35, 0.16, 7.7],
          dark,
        )
        this.add(
          `datacentre-overhead-guide-${id}`,
          'box',
          [x, this.config.height - 0.54, -0.05],
          [0.16, 0.06, 7.25],
          accent,
        )
      }
      dressing.addCeilingRaft(
        'datacentre-ceiling-west-aisle',
        -8.5,
        0,
        3.4,
        8.2,
        this.config.height,
        dark,
        accent,
      )
      dressing.addCeilingRaft(
        'datacentre-ceiling-east-aisle',
        5.5,
        0,
        3.4,
        8.2,
        this.config.height,
        dark,
        accent,
      )
      dressing.addCeilingRaft(
        'datacentre-ceiling-core',
        0,
        -6.5,
        5.2,
        5.2,
        this.config.height,
        dark,
        accent,
      )
    } else {
      dressing.addCeilingRaft(`${this.config.id}-ceiling-west`, -7, 0, 8, 7, this.config.height, dark, accent)
      dressing.addCeilingRaft(`${this.config.id}-ceiling-east`, 7, 0, 8, 7, this.config.height, dark, accent)
      dressing.addPlanter(`${this.config.id}-entry-planter-west`, -3.2, this.config.halfZ - 1.6, metal, foliage)
      dressing.addPlanter(`${this.config.id}-entry-planter-east`, 3.2, this.config.halfZ - 1.6, metal, foliage)
    }

    if (this.config.id === 'exchange') {
      dressing.addGlassRoom(
        'exchange-cost-studio',
        EXCHANGE_COST_STUDIO.centerX,
        EXCHANGE_COST_STUDIO.centerZ,
        EXCHANGE_COST_STUDIO.width,
        EXCHANGE_COST_STUDIO.depth,
        glass,
        metal,
      )
      this.add(
        'exchange-flow-interaction-edge',
        'box',
        [EXCHANGE_LIVE_FLOW_INTERACTION.x, 0.028, EXCHANGE_LIVE_FLOW_INTERACTION.z],
        [2.4, 0.056, 0.12],
        accent,
      )
      this.add(
        'exchange-cost-threshold',
        'box',
        [-9, 0.024, -0.65],
        [1.5, 0.048, 0.12],
        accent,
      )
      this.add(
        'exchange-capacity-review-track',
        'box',
        [9.05, 0.024, -4.8],
        [5.9, 0.048, 4.4],
        soft,
      )
    } else if (this.config.id === 'workshop') {
      this.workshopMaterials = {
        buildActive: material([0.96, 0.57, 0.22], 1.6),
        buildIdle: material([0.25, 0.29, 0.33]),
        pluginHealthy: material([0.24, 0.72, 0.78], 1.6),
        pluginDegraded: material([0.96, 0.57, 0.22], 1.6),
        pluginDown: material([0.86, 0.24, 0.22], 1.6),
        pluginUnknown: material([0.34, 0.38, 0.43]),
      }
      // Licensed desks/monitors own the build surfaces. Small floor rails sit
      // at each inner station edge instead of intersecting the screens as the
      // previous orange tool slabs did.
      for (const [side, x] of [['west', -7.02], ['east', 7.02]] as const) {
        for (const [row, z] of [['south', 3], ['north', -0.5]] as const) {
          const signal = this.add(
            `workshop-build-status-${side}-${row}`,
            'box',
            [x, 0.055, z],
            [0.08, 0.045, 1.25],
            this.workshopMaterials.buildIdle,
          )
          signal.tags.add('workshop-build-signal', 'workshop-build-inactive')
          this.workshopBuildSignals.push(signal)
        }
      }
      dressing.addGlassRoom(
        'workshop-test-cell',
        WORKSHOP_TEST_CELL.centerX,
        WORKSHOP_TEST_CELL.centerZ,
        WORKSHOP_TEST_CELL.width,
        WORKSHOP_TEST_CELL.depth,
        glass,
        metal,
      )
      // The licensed round table supplies the physical test surface. This
      // compact prototype assembly is deliberately smaller than a monitor and
      // leaves the door/approach readable.
      this.add(
        'workshop-test-prototype-base',
        'cylinder',
        [WORKSHOP_TEST_TABLE.x, 0.82, WORKSHOP_TEST_TABLE.z],
        [0.72, 0.14, 0.72],
        dark,
      )
      this.add(
        'workshop-test-prototype-core',
        'sphere',
        [WORKSHOP_TEST_TABLE.x, 1.12, WORKSHOP_TEST_TABLE.z],
        [0.34, 0.34, 0.34],
        material([0.24, 0.72, 0.78], 1.2),
      )
      this.add(
        'workshop-tool-bay-label',
        'box',
        [-10.1, 2.35, -9.35],
        [5.2, 0.12, 0.10],
        material([0.84, 0.48, 0.18], 1.1),
      )
      this.add(
        'workshop-plugin-bay-label',
        'box',
        [10.1, 2.35, -9.35],
        [5.2, 0.12, 0.10],
        material([0.24, 0.72, 0.78], 1.1),
      )
    } else if (this.config.id === 'cafe') {
      // Licensed furniture owns all human-scale surfaces. Architecture names
      // the service bar, the common-table interaction edge, and a measured
      // glass quiet room without placing decorative slabs through the props.
      this.add(
        'cafe-counter-canopy',
        'box',
        [CAFE_COUNTER_CANOPY.x, CAFE_COUNTER_CANOPY.y, CAFE_COUNTER_CANOPY.z],
        [
          CAFE_COUNTER_CANOPY.width,
          CAFE_COUNTER_CANOPY.height,
          CAFE_COUNTER_CANOPY.depth,
        ],
        dark,
      )
      this.add('cafe-counter-pickup-light', 'box', [-9.8, 2.48, -7.88], [4.8, 0.07, 0.08], accent)
      this.add('cafe-common-interaction-edge', 'box',
        [CAFE_COMMON_TABLE.actionX, 0.025, CAFE_COMMON_TABLE.actionZ],
        [2.2, 0.05, 0.12], accent)
      dressing.addGlassRoom(
        'cafe-quiet-room',
        CAFE_QUIET_ROOM.centerX,
        CAFE_QUIET_ROOM.centerZ,
        CAFE_QUIET_ROOM.width,
        CAFE_QUIET_ROOM.depth,
        glass,
        metal,
      )
      this.add('cafe-quiet-threshold', 'box',
        [CAFE_QUIET_ROOM.centerX, 0.025, CAFE_QUIET_ROOM.frontZ],
        [1.5, 0.05, 0.12], accent)
    } else {
      this.datacentreMaterials = {
        worldReady: material([0.22, 0.72, 1], 2.2),
        worldConnecting: material([0.96, 0.62, 0.22], 1.8),
        serviceHealthy: material([0.18, 0.82, 0.58], 1.8),
        serviceDegraded: material([0.96, 0.62, 0.22], 1.8),
        serviceDown: material([0.90, 0.22, 0.24], 1.8),
        serviceUnknown: material([0.42, 0.48, 0.58], 1.1),
        inactive: material([0.12, 0.16, 0.22]),
      }
      for (const rack of DATACENTRE_SERVICE_RACKS) {
        const signal = this.add(
          `datacentre-rack-light-${rack.id}`,
          'box',
          [rack.x, 1.55, rack.z + (rack.rotY === 0 ? 0.53 : -0.53)],
          [0.78, 0.10, 0.06],
          this.datacentreMaterials.inactive,
        )
        signal.tags.add('datacentre-service-signal', 'datacentre-service-inactive')
        this.datacentreServiceSignals.push(signal)
      }
      this.add('datacentre-world-dais', 'cylinder', [0, 0.14, -6.5], [4.4, 0.28, 4.4], dark)
      this.add('datacentre-world-ring-outer', 'cylinder', [0, 0.31, -6.5], [3.65, 0.08, 3.65], metal)
      this.add('datacentre-world-ring-inner', 'cylinder', [0, 0.40, -6.5], [2.85, 0.10, 2.85], accent)
      this.datacentreWorldCore = this.add(
        'datacentre-world-core',
        'sphere',
        [0, 1.72, -6.5],
        [0.68, 1.08, 0.68],
        this.datacentreMaterials.inactive,
      )
      for (const [id, x, z] of [
        ['west', -1.65, -6.5],
        ['east', 1.65, -6.5],
        ['north', 0, -8.15],
        ['south', 0, -4.85],
      ] as const) {
        this.add(
          `datacentre-world-pylon-${id}`,
          'cylinder',
          [x, 1.08, z],
          [0.12, 1.55, 0.12],
          metal,
        )
        this.add(
          `datacentre-world-node-${id}`,
          'sphere',
          [x, 1.96, z],
          [0.18, 0.18, 0.18],
          accent,
        )
      }
      dressing.addGlassRoom(
        'datacentre-noc',
        DATACENTRE_NOC_ROOM.centerX,
        DATACENTRE_NOC_ROOM.centerZ,
        DATACENTRE_NOC_ROOM.width,
        DATACENTRE_NOC_ROOM.depth,
        glass,
        metal,
      )
      for (const [id, title, subtitle, x] of [
        ['world', 'WORLD LINK', 'PRESENCE / STATE', 6.2],
        ['daemon', 'DAEMON', 'RUNTIME HEALTH', 8.5],
        ['services', 'SERVICES', 'AUTHORIZED EDGE', 10.8],
      ] as const) {
        this.add(
          `datacentre-noc-status-wall-${id}`,
          'box',
          [x, 1.75, 3.48],
          [1.85, 1.25, 0.07],
          textMaterial(this.app, title, subtitle, '#60a5fa'),
        )
      }
      this.add('datacentre-noc-console-line', 'box', [8.5, 0.08, 5.05], [5.4, 0.04, 0.08], accent)
      this.add(
        'datacentre-cable-spine',
        'box',
        [
          (DATACENTRE_CABLE_SPINE_COLLIDER.minX + DATACENTRE_CABLE_SPINE_COLLIDER.maxX) / 2,
          DATACENTRE_CABLE_SPINE_COLLIDER.maxY / 2,
          (DATACENTRE_CABLE_SPINE_COLLIDER.minZ + DATACENTRE_CABLE_SPINE_COLLIDER.maxZ) / 2,
        ],
        [
          DATACENTRE_CABLE_SPINE_COLLIDER.maxX - DATACENTRE_CABLE_SPINE_COLLIDER.minX,
          DATACENTRE_CABLE_SPINE_COLLIDER.maxY,
          DATACENTRE_CABLE_SPINE_COLLIDER.maxZ - DATACENTRE_CABLE_SPINE_COLLIDER.minZ,
        ],
        accent,
      )
      for (const marker of [
        {
          id: 'world-core',
          title: 'WORLD CORE',
          subtitle: 'ARENA WORLD LINK',
          position: [0, 2.92, -9.65] as const,
          scale: [4.2, 0.46, 0.07] as const,
        },
        {
          id: 'noc-daemon',
          title: 'NOC / DAEMON',
          subtitle: 'RUNTIME OPERATIONS',
          position: [DATACENTRE_NOC_ROOM.centerX, 2.94, 9.45] as const,
          scale: [3.6, 0.42, 0.07] as const,
        },
        {
          id: 'service-aisles',
          title: 'SERVICE AISLES',
          subtitle: 'AUTHORIZED SERVICES',
          position: [-8.5, 2.90, -3.85] as const,
          scale: [3.9, 0.42, 0.07] as const,
        },
      ]) {
        const markerEntity = this.add(
          `datacentre-program-wayfinding-${marker.id}`,
          'box',
          marker.position,
          marker.scale,
          textMaterial(this.app, marker.title, marker.subtitle, '#60a5fa'),
        )
        markerEntity.tags.add('datacentre-program-wayfinding')
      }
    }
  }

  private sync(): void {
    const state = useArenaStore.getState()
    const exchangeProjection = this.config.id === 'exchange'
      ? this.projectExchange(state)
      : undefined
    const workshopProjection = this.config.id === 'workshop'
      ? this.projectWorkshop(state)
      : undefined
    const cafeProjection = this.config.id === 'cafe'
      ? this.projectCafe(state)
      : undefined
    const datacentreProjection = this.config.id === 'datacentre'
      ? this.projectDatacentre(state)
      : undefined
    const view = campusRoomView(this.config.id, {
      status: state.remoteProjectionStatus,
      worldStatus: state.worldStateStatus,
      health: state.health,
      runs: state.runs,
      agents: state.agents,
      chats: state.chats,
    })
    const lines = exchangeProjection
      ? exchangeProjectionLines(exchangeProjection)
      : workshopProjection
      ? workshopProjectionLines(workshopProjection)
      : cafeProjection
      ? cafeProjectionLines(cafeProjection)
      : datacentreProjection
      ? datacentreProjectionLines(datacentreProjection)
      : view.lines
    const datacentreReady = datacentreProjection?.applicationStatus === 'fresh' &&
      datacentreProjection.worldStatus === 'ready' &&
      datacentreProjection.servicesProjected
    const stateLabel = datacentreProjection
      ? datacentreReady
        ? 'LIVE PROJECTION'
        : `APPLICATION ${datacentreProjection.applicationStatus.toUpperCase()} · WORLD ${datacentreProjection.worldStatus.toUpperCase()}`
      : view.state === 'ready' ? 'LIVE PROJECTION' : view.state.toUpperCase()
    this.status.textContent = `${this.config.title} · ${stateLabel}\n${lines.join('\n')}`
    this.status.style.borderColor = datacentreProjection
      ? datacentreReady
        ? 'rgba(34,211,238,.55)'
        : datacentreProjection.applicationStatus === 'disconnected' ||
          datacentreProjection.worldStatus === 'disconnected'
          ? 'rgba(248,113,113,.55)'
          : 'rgba(251,191,36,.55)'
      : view.state === 'ready'
        ? 'rgba(34,211,238,.55)'
        : view.state === 'loading'
          ? 'rgba(251,191,36,.55)'
          : 'rgba(248,113,113,.55)'
    this.rebuildIndicators(
      exchangeProjection,
      workshopProjection,
      cafeProjection,
      datacentreProjection,
    )
    if (cafeProjection) this.syncCafeCharacterStates(state)
  }

  private rebuildIndicators(
    exchangeProjection?: ExchangeProjectionState,
    workshopProjection?: WorkshopProjectionState,
    cafeProjection?: CafeProjectionState,
    datacentreProjection?: DatacentreProjectionState,
  ): void {
    if (this.config.id === 'exchange') {
      if (exchangeProjection) this.updateExchangeProjection(exchangeProjection)
      return
    }
    if (this.config.id === 'workshop') {
      if (workshopProjection) this.updateWorkshopProjection(workshopProjection)
      return
    }
    if (this.config.id === 'cafe') {
      if (cafeProjection) this.updateCafeProjection(cafeProjection)
      return
    }
    if (this.config.id === 'datacentre') {
      if (datacentreProjection) this.updateDatacentreProjection(datacentreProjection)
    }
  }

  private buildExchangeSignals(): void {
    this.exchangeMaterials = {
      active: material([0.15, 0.82, 0.88], 2),
      idle: material([0.16, 0.20, 0.25]),
      degraded: material([0.96, 0.62, 0.22], 1.4),
    }
    for (let index = 0; index < MAX_EXCHANGE_RUNS; index++) {
      const column = index % 4
      const row = Math.floor(index / 4)
      const signal = this.add(
        `exchange-flow-signal-${index}`,
        'box',
        [-1.35 + column * 0.9, 0.79, -4.9 + row * 0.62],
        [0.56, 0.055, 0.18],
        this.exchangeMaterials.idle,
        this.indicators,
      )
      signal.tags.add('exchange-run-signal', 'exchange-signal-inactive')
      this.exchangeRunSignals.push(signal)
    }
    for (let index = 0; index < MAX_EXCHANGE_RUNS; index++) {
      const signal = this.add(
        `exchange-capacity-signal-${index}`,
        'box',
        [5.55 + index * 0.72, 0.48, -8.38],
        [0.38, 0.18, 0.06],
        this.exchangeMaterials.idle,
        this.indicators,
      )
      signal.tags.add('exchange-capacity-signal', 'exchange-signal-inactive')
      this.exchangeCapacitySignals.push(signal)
    }
  }

  private projectExchange(
    state: ReturnType<typeof useArenaStore.getState>,
  ): ExchangeProjectionState {
    const next = layoutExchangeProjection(
      state.remoteProjectionStatus,
      state.health,
      state.runs,
      this.exchangeLastGood,
    )
    if (state.remoteProjectionStatus === 'fresh') this.exchangeLastGood = next
    return next
  }

  private updateExchangeProjection(next: ExchangeProjectionState): void {
    const key = exchangeProjectionKey(next)
    if (key === this.exchangeSemanticKey) return
    this.exchangeSemanticKey = key
    const materials = this.exchangeMaterials
    if (!materials) return
    const degraded = next.state === 'loading' ||
      next.state === 'stale-last-good' ||
      next.state === 'disconnected'
    for (let index = 0; index < this.exchangeRunSignals.length; index++) {
      const signal = this.exchangeRunSignals[index]
      const active = index < next.runs.length
      signal.tags.remove('exchange-signal-active', 'exchange-signal-inactive')
      signal.tags.add(active ? 'exchange-signal-active' : 'exchange-signal-inactive')
      this.setRenderMaterial(
        signal,
        active ? (degraded ? materials.degraded : materials.active) : materials.idle,
      )
    }
    const activeCapacity = Math.min(
      MAX_EXCHANGE_RUNS,
      next.capacity?.active ?? 0,
    )
    for (let index = 0; index < this.exchangeCapacitySignals.length; index++) {
      const signal = this.exchangeCapacitySignals[index]
      const active = index < activeCapacity
      signal.tags.remove('exchange-signal-active', 'exchange-signal-inactive')
      signal.tags.add(active ? 'exchange-signal-active' : 'exchange-signal-inactive')
      this.setRenderMaterial(
        signal,
        active ? (degraded ? materials.degraded : materials.active) : materials.idle,
      )
    }
    this.paintExchangeStatus(next)
  }

  private paintExchangeStatus(projection: ExchangeProjectionState): void {
    const lines = exchangeProjectionLines(projection)
    const titles = ['COST WINDOW', 'LIVE FLOW', 'CAPACITY']
    const notes = [
      projection.costSummary
        ? `PROJECTED WINDOW · ${projection.costSummary.since.toUpperCase()}`
        : 'PORTAL AGGREGATE ONLY',
      projection.runOverflow && projection.runOverflow > 0
        ? `+${projection.runOverflow} RUNS NOT SHOWN`
        : 'RUNNING + QUEUED WORK',
      projection.capacity
        ? 'PORTAL-OWNED AGENT POOL'
        : 'CAPACITY NOT AVAILABLE',
    ]
    const degraded = projection.state !== 'fresh-empty' &&
      projection.state !== 'fresh-populated'
    for (let index = 0; index < this.exchangeStatusEntities.length; index++) {
      const entity = this.exchangeStatusEntities[index]
      const statusMaterial = this.exchangeStatusMaterials[index]
      entity.tags.remove(
        'exchange-status-fresh-empty',
        'exchange-status-fresh-populated',
        'exchange-status-loading',
        'exchange-status-stale-last-good',
        'exchange-status-disconnected',
        'exchange-run-overflow',
        'exchange-capacity-overflow',
      )
      entity.tags.add(`exchange-status-${projection.state}`)
      if (index === 1 && (projection.runOverflow ?? 0) > 0) {
        entity.tags.add('exchange-run-overflow')
      }
      if (index === 2 && (projection.capacity?.active ?? 0) > MAX_EXCHANGE_RUNS) {
        entity.tags.add('exchange-capacity-overflow')
      }
      Object.assign(entity, {
        exchangeProjectionLine: lines[index],
        exchangeProjectionState: projection.state,
      })
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 640
        canvas.height = 180
        const context = canvas.getContext('2d')
        if (!context) continue
        context.fillStyle = '#0b1119'
        context.fillRect(0, 0, canvas.width, canvas.height)
        context.fillStyle = degraded ? '#f0a24b' : '#22d3ee'
        context.fillRect(0, 0, 14, canvas.height)
        context.fillStyle = '#f2f5fa'
        context.font = '700 40px Nunito, system-ui, sans-serif'
        context.fillText(titles[index], 42, 58)
        context.fillStyle = degraded ? '#f4c081' : '#a6e8ed'
        context.font = '600 21px ui-monospace, monospace'
        context.fillText(lines[index], 42, 108)
        context.fillStyle = '#8494a8'
        context.font = '600 17px ui-monospace, monospace'
        context.fillText(notes[index], 42, 148)
        const texture = new pc.Texture(this.app.graphicsDevice, {
          width: canvas.width,
          height: canvas.height,
        })
        texture.setSource(canvas)
        const previous = this.exchangeStatusTextures[index]
        this.exchangeStatusTextures[index] = texture
        statusMaterial.diffuseMap = texture
        statusMaterial.emissiveMap = texture
        statusMaterial.emissive = new pc.Color(1, 1, 1)
        statusMaterial.emissiveIntensity = 0.85
        statusMaterial.update()
        previous?.destroy()
      } catch {
        // Static titled surfaces remain if canvas text is unavailable.
      }
    }
  }

  private buildWorkshopPluginSlots(): void {
    const materials = this.workshopMaterials
    if (!materials) return
    for (let index = 0; index < MAX_WORKSHOP_PLUGINS; index++) {
      const slot = this.add(
        `workshop-plugin-slot-${index}`,
        'box',
        [7.55 + index * 0.72, 1.02, -9.28],
        [0.46, 0.46, 0.08],
        materials.pluginUnknown,
        this.indicators,
      )
      slot.enabled = false
      slot.tags.add('workshop-plugin-slot')
      this.workshopPluginSlots.push(slot)
    }
  }

  private buildCafePresenceSignals(): void {
    this.cafeMaterials = {
      active: material([0.96, 0.48, 0.55], 1.8),
      idle: material([0.20, 0.15, 0.17]),
      degraded: material([0.96, 0.62, 0.22], 1.4),
    }
    for (let index = 0; index < MAX_CAFE_AGENTS; index++) {
      const slot = CAFE_AGENT_SLOTS[index]
      const signal = this.add(
        `cafe-presence-slot-${slot.id}`,
        'cylinder',
        [slot.x, 0.018, slot.z],
        [0.68, 0.035, 0.68],
        this.cafeMaterials.idle,
        this.indicators,
      )
      signal.tags.add('cafe-presence-signal', 'cafe-presence-inactive')
      this.cafePresenceSignals.push(signal)
    }
  }

  private projectCafe(
    state: ReturnType<typeof useArenaStore.getState>,
  ): CafeProjectionState {
    const next = layoutCafeProjection(
      state.remoteProjectionStatus,
      state.agents,
      state.chats,
      state.runs,
      this.cafeLastGood,
    )
    if (state.remoteProjectionStatus === 'fresh') this.cafeLastGood = next
    return next
  }

  private updateCafeProjection(next: CafeProjectionState): void {
    const key = cafeProjectionKey(next)
    if (key === this.cafeSemanticKey) return
    this.cafeSemanticKey = key
    const materials = this.cafeMaterials
    if (!materials) return
    const degraded =
      next.state === 'loading' ||
      next.state === 'stale-last-good' ||
      next.state === 'disconnected'
    for (let index = 0; index < this.cafePresenceSignals.length; index++) {
      const signal = this.cafePresenceSignals[index]
      const active = index < next.agents.length
      signal.tags.remove('cafe-presence-active', 'cafe-presence-inactive')
      signal.tags.add(active ? 'cafe-presence-active' : 'cafe-presence-inactive')
      this.setRenderMaterial(
        signal,
        active
          ? degraded ? materials.degraded : materials.active
          : materials.idle,
      )
    }

    const agentKey = JSON.stringify(next.agents.map((entry) => [
      entry.agent.id,
      entry.agent.name,
      entry.slot.id,
      entry.boundRunId ?? null,
    ]))
    if (agentKey !== this.cafeAgentKey) {
      this.cafeAgentKey = agentKey
      for (const character of this.cafeCharacters.values()) character.destroy()
      this.cafeCharacters.clear()
      const origin = this.root.getPosition()
      this.roster = next.agents.map((entry) => {
        const character = spawnSyntyRosterCharacter(
          this.app,
          [entry.slot.stationX, 0, entry.slot.stationZ],
          entry.agent.id,
          pickVariant(entry.agent.id),
          {
            parent: this.root,
            ownerTags: [
              'arena-character-owner',
              'arena-cafe-character',
              `arena-cafe-agent:${entry.agent.id}`,
              `arena-cafe-slot:${entry.slot.id}`,
            ],
            dynamicBounds: true,
          },
        )
        character.setStation({
          position: [entry.slot.stationX, 0, entry.slot.stationZ],
          rotY: entry.slot.stationRotY,
        })
        character.setActive?.(this.active)
        this.cafeCharacters.set(entry.agent.id, character)
        return {
          id: `agent:${entry.agent.id}`,
          agentId: entry.agent.id,
          name: entry.agent.name,
          room: entry.slot.room,
          worldPos: [entry.slot.x, 0, origin.z + entry.slot.z],
          ...(entry.boundRunId ? { boundRunId: entry.boundRunId } : {}),
        }
      })
    }
    this.paintCafeStatus(next)
  }

  private syncCafeCharacterStates(
    state: ReturnType<typeof useArenaStore.getState>,
  ): void {
    for (const agent of this.roster) {
      const character = this.cafeCharacters.get(agent.agentId)
      if (!character) continue
      const run = agent.boundRunId
        ? state.runs.find((candidate) => candidate.id === agent.boundRunId)
        : undefined
      character.setAnimState(
        run
          ? computeAvatarState(
              run,
              state.interactionsByRun[run.id] ?? [],
              state.transcriptsByRun[run.id] ?? [],
            )
          : 'idle',
      )
    }
  }

  private paintCafeStatus(projection: CafeProjectionState): void {
    const lines = cafeProjectionLines(projection)
    const titles = ['TEAM PRESENCE', 'BOUND CHATS', 'MESSAGE ACCESS']
    const notes = [
      (projection.agentOverflow ?? 0) > 0
        ? `+${projection.agentOverflow} IN PHONE DIRECTORY`
        : 'VISIBLE SPATIAL CAPACITY · 6',
      'EXACT PORTAL AGENT BINDINGS ONLY',
      'CANONICAL SEND AUTHORIZATION',
    ]
    const degraded = projection.state !== 'fresh-empty' &&
      projection.state !== 'fresh-populated'
    for (let index = 0; index < this.cafeStatusEntities.length; index++) {
      const entity = this.cafeStatusEntities[index]
      const statusMaterial = this.cafeStatusMaterials[index]
      entity.tags.remove(
        'cafe-status-fresh-empty',
        'cafe-status-fresh-populated',
        'cafe-status-loading',
        'cafe-status-stale-last-good',
        'cafe-status-disconnected',
        'cafe-agent-overflow',
      )
      entity.tags.add(`cafe-status-${projection.state}`)
      if (index === 0 && (projection.agentOverflow ?? 0) > 0) {
        entity.tags.add('cafe-agent-overflow')
      }
      Object.assign(entity, {
        cafeProjectionLine: lines[index],
        cafeProjectionState: projection.state,
      })
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 640
        canvas.height = 180
        const context = canvas.getContext('2d')
        if (!context) continue
        context.fillStyle = '#171015'
        context.fillRect(0, 0, canvas.width, canvas.height)
        context.fillStyle = degraded ? '#f0a24b' : '#fb7185'
        context.fillRect(0, 0, 14, canvas.height)
        context.fillStyle = '#fff4f5'
        context.font = '700 40px Nunito, system-ui, sans-serif'
        context.fillText(titles[index], 42, 58)
        context.fillStyle = degraded ? '#f4c081' : '#fecdd3'
        context.font = '600 20px ui-monospace, monospace'
        context.fillText(lines[index], 42, 108)
        context.fillStyle = '#a38b96'
        context.font = '600 17px ui-monospace, monospace'
        context.fillText(notes[index], 42, 148)
        const texture = new pc.Texture(this.app.graphicsDevice, {
          width: canvas.width,
          height: canvas.height,
        })
        texture.setSource(canvas)
        const previous = this.cafeStatusTextures[index]
        this.cafeStatusTextures[index] = texture
        statusMaterial.diffuseMap = texture
        statusMaterial.emissiveMap = texture
        statusMaterial.emissive = new pc.Color(1, 1, 1)
        statusMaterial.emissiveIntensity = 0.85
        statusMaterial.update()
        previous?.destroy()
      } catch {
        // Static titled surfaces remain if canvas text is unavailable.
      }
    }
  }

  private projectDatacentre(
    state: ReturnType<typeof useArenaStore.getState>,
  ): DatacentreProjectionState {
    const next = layoutDatacentreProjection(
      state.remoteProjectionStatus,
      state.worldStateStatus,
      state.health,
      this.datacentreLastGood,
    )
    if (state.remoteProjectionStatus === 'fresh' && next.servicesProjected) {
      this.datacentreLastGood = next
    }
    return next
  }

  private updateDatacentreProjection(next: DatacentreProjectionState): void {
    const key = datacentreProjectionKey(next)
    if (key === this.datacentreSemanticKey) return
    this.datacentreSemanticKey = key
    const materials = this.datacentreMaterials
    if (!materials) return

    if (this.datacentreWorldCore) {
      this.datacentreWorldCore.tags.remove(
        'datacentre-world-ready',
        'datacentre-world-connecting',
        'datacentre-world-degraded',
        'datacentre-world-disconnected',
      )
      this.datacentreWorldCore.tags.add(`datacentre-world-${next.worldStatus}`)
      this.setRenderMaterial(
        this.datacentreWorldCore,
        next.worldStatus === 'ready'
          ? materials.worldReady
          : next.worldStatus === 'connecting'
            ? materials.worldConnecting
            : next.worldStatus === 'degraded'
              ? materials.serviceDegraded
              : materials.serviceDown,
      )
      Object.assign(this.datacentreWorldCore, {
        datacentreWorldStatus: next.worldStatus,
      })
    }

    for (let index = 0; index < this.datacentreServiceSignals.length; index++) {
      const signal = this.datacentreServiceSignals[index]
      const service = next.services[index]
      signal.tags.remove(
        'datacentre-service-healthy',
        'datacentre-service-degraded',
        'datacentre-service-down',
        'datacentre-service-unknown',
        'datacentre-service-inactive',
      )
      const status = service?.status ?? 'inactive'
      signal.tags.add(`datacentre-service-${status}`)
      this.setRenderMaterial(
        signal,
        status === 'healthy'
          ? materials.serviceHealthy
          : status === 'degraded'
            ? materials.serviceDegraded
            : status === 'down'
              ? materials.serviceDown
              : status === 'unknown'
                ? materials.serviceUnknown
                : materials.inactive,
      )
      Object.assign(signal, {
        datacentreServiceId: service?.id ?? null,
        datacentreServiceName: service?.name ?? null,
        datacentreServiceKind: service?.kind ?? null,
        datacentreServiceStatus: service?.status ?? null,
      })
    }

    this.paintDatacentreStatus(next)
  }

  private paintDatacentreStatus(projection: DatacentreProjectionState): void {
    const lines = datacentreProjectionLines(projection)
    const titles = ['WORLD SERVER', 'DAEMON / RUNTIME', 'AUTHORIZED SERVICES']
    const notes = [
      'ARENA-OWNED WORLD LINK',
      'PORTAL APPLICATION PROJECTION',
      projection.serviceOverflow
        ? `+${projection.serviceOverflow} SERVICES IN SYSTEMS`
        : 'BOUNDED PHYSICAL SERVICE VIEW',
    ]
    const degraded = projection.state !== 'fresh-empty' &&
      projection.state !== 'fresh-populated'
    for (let index = 0; index < this.datacentreStatusEntities.length; index++) {
      const entity = this.datacentreStatusEntities[index]
      const statusMaterial = this.datacentreStatusMaterials[index]
      entity.tags.remove(
        'datacentre-status-fresh-empty',
        'datacentre-status-fresh-populated',
        'datacentre-status-fresh-unavailable',
        'datacentre-status-loading',
        'datacentre-status-stale-last-good',
        'datacentre-status-disconnected',
        'datacentre-service-overflow',
      )
      entity.tags.add(`datacentre-status-${projection.state}`)
      if (index === 2 && (projection.serviceOverflow ?? 0) > 0) {
        entity.tags.add('datacentre-service-overflow')
      }
      Object.assign(entity, {
        datacentreProjectionLine: lines[index],
        datacentreProjectionState: projection.state,
        datacentreApplicationStatus: projection.applicationStatus,
        datacentreWorldStatus: projection.worldStatus,
        datacentreTotalServices: projection.totalServices,
        datacentreServiceOverflow: projection.serviceOverflow,
      })
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 760
        canvas.height = 180
        const context = canvas.getContext('2d')
        if (!context) continue
        context.fillStyle = '#08111c'
        context.fillRect(0, 0, canvas.width, canvas.height)
        const stateColor = index === 0
          ? projection.worldStatus === 'ready'
            ? '#38bdf8'
            : projection.worldStatus === 'connecting'
              ? '#f0a24b'
              : projection.worldStatus === 'degraded'
                ? '#f59e0b'
                : '#ef4444'
          : index === 1
            ? projection.state === 'disconnected'
              ? '#ef4444'
              : degraded
                ? '#f0a24b'
                : projection.daemonStatus === 'healthy'
                  ? '#60a5fa'
                  : projection.daemonStatus === 'degraded'
                    ? '#f59e0b'
                    : projection.daemonStatus === 'down'
                      ? '#ef4444'
                      : '#64748b'
            : projection.state === 'disconnected'
              ? '#ef4444'
              : degraded
                ? '#f0a24b'
                : (projection.downServices ?? 0) > 0
                  ? '#ef4444'
                  : (projection.degradedServices ?? 0) > 0
                    ? '#f0a24b'
                    : '#60a5fa'
        context.fillStyle = stateColor
        context.fillRect(0, 0, 14, canvas.height)
        context.fillStyle = '#f2f7ff'
        context.font = '700 38px Nunito, system-ui, sans-serif'
        context.fillText(titles[index], 42, 56)
        context.fillStyle = degraded ? '#f4c081' : '#bfdbfe'
        context.font = '600 17px ui-monospace, monospace'
        context.fillText(lines[index], 42, 108)
        context.fillStyle = '#8494a8'
        context.font = '600 17px ui-monospace, monospace'
        context.fillText(notes[index], 42, 148)
        const texture = new pc.Texture(this.app.graphicsDevice, {
          width: canvas.width,
          height: canvas.height,
        })
        texture.setSource(canvas)
        const previous = this.datacentreStatusTextures[index]
        this.datacentreStatusTextures[index] = texture
        statusMaterial.diffuseMap = texture
        statusMaterial.emissiveMap = texture
        statusMaterial.emissive = new pc.Color(1, 1, 1)
        statusMaterial.emissiveIntensity = 0.85
        statusMaterial.update()
        previous?.destroy()
      } catch {}
    }
  }

  private setRenderMaterial(entity: pc.Entity, value: pc.StandardMaterial): void {
    for (const mesh of entity.render?.meshInstances ?? []) mesh.material = value
  }

  private projectWorkshop(
    state: ReturnType<typeof useArenaStore.getState>,
  ): WorkshopProjectionState {
    const next = layoutWorkshopProjection(
      state.remoteProjectionStatus,
      state.runs,
      state.health?.plugins ?? [],
      this.workshopLastGood,
    )
    if (state.remoteProjectionStatus === 'fresh') this.workshopLastGood = next
    return next
  }

  private updateWorkshopProjection(next: WorkshopProjectionState): void {
    const key = workshopProjectionKey(next)
    if (key === this.workshopSemanticKey) return
    this.workshopSemanticKey = key
    const materials = this.workshopMaterials
    if (!materials) return
    for (let index = 0; index < this.workshopBuildSignals.length; index++) {
      const signal = this.workshopBuildSignals[index]
      const active = index < next.builds.length
      signal.tags.remove('workshop-build-active', 'workshop-build-inactive')
      signal.tags.add(active ? 'workshop-build-active' : 'workshop-build-inactive')
      this.setRenderMaterial(
        signal,
        active ? materials.buildActive : materials.buildIdle,
      )
    }
    for (let index = 0; index < this.workshopPluginSlots.length; index++) {
      const slot = this.workshopPluginSlots[index]
      const plugin = next.plugins[index]
      slot.tags.remove(
        'workshop-plugin-healthy',
        'workshop-plugin-degraded',
        'workshop-plugin-down',
        'workshop-plugin-unknown',
      )
      if (!plugin) {
        slot.enabled = false
        continue
      }
      slot.enabled = true
      slot.tags.add(`workshop-plugin-${plugin.status}`)
      this.setRenderMaterial(
        slot,
        plugin.status === 'healthy'
          ? materials.pluginHealthy
          : plugin.status === 'degraded'
            ? materials.pluginDegraded
            : plugin.status === 'down'
              ? materials.pluginDown
              : materials.pluginUnknown,
      )
    }
    this.paintWorkshopStatus(next)
  }

  private paintWorkshopStatus(projection: WorkshopProjectionState): void {
    const entity = this.workshopStatusEntity
    const statusMaterial = this.workshopStatusMaterial
    if (!entity || !statusMaterial) return
    entity.tags.remove(
      'workshop-status-fresh-empty',
      'workshop-status-fresh-populated',
      'workshop-status-loading',
      'workshop-status-stale',
      'workshop-status-disconnected',
      'workshop-build-overflow',
      'workshop-plugin-overflow',
    )
    entity.tags.add(`workshop-status-${projection.state}`)
    if (projection.buildOverflow > 0) entity.tags.add('workshop-build-overflow')
    if (projection.pluginOverflow > 0) entity.tags.add('workshop-plugin-overflow')
    try {
      const canvas = document.createElement('canvas')
      canvas.width = 640
      canvas.height = 180
      const context = canvas.getContext('2d')
      if (!context) return
      const degraded = projection.state !== 'fresh-empty' &&
        projection.state !== 'fresh-populated'
      context.fillStyle = '#0e1419'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.fillStyle = degraded ? '#f0a24b' : '#55c6d0'
      context.fillRect(0, 0, 14, canvas.height)
      context.fillStyle = '#f2f5fa'
      context.font = '700 42px Nunito, system-ui, sans-serif'
      context.fillText('PLUGIN BAY', 42, 58)
      context.fillStyle = degraded ? '#f4c081' : '#a6dce2'
      context.font = '600 20px ui-monospace, monospace'
      const lines = workshopProjectionLines(projection)
      context.fillText(lines[0], 42, 96)
      context.fillText(lines[1], 42, 126)
      const overflow = [
        projection.buildOverflow ? `+${projection.buildOverflow} BUILDS` : '',
        projection.pluginOverflow ? `+${projection.pluginOverflow} PLUGINS` : '',
      ].filter(Boolean).join(' · ')
      context.fillText(overflow || lines[2], 42, 156)
      const texture = new pc.Texture(this.app.graphicsDevice, {
        width: canvas.width,
        height: canvas.height,
      })
      texture.setSource(canvas)
      const previous = this.workshopStatusTexture
      this.workshopStatusTexture = texture
      statusMaterial.diffuseMap = texture
      statusMaterial.emissiveMap = texture
      statusMaterial.emissive = new pc.Color(1, 1, 1)
      statusMaterial.emissiveIntensity = 0.85
      statusMaterial.update()
      previous?.destroy()
    } catch {
      // The physical cyan/amber status panel remains if canvas text is absent.
    }
  }

  frame(active: boolean): void {
    this.active = active
    if (this.overlay) this.overlay.element.style.display = active ? 'block' : 'none'
    this.updateAction(active)
    if (this.config.id === 'cafe') {
      for (const character of this.cafeCharacters.values()) {
        character.setActive?.(active)
        Object.assign(character.entity, {
          arenaCafeVisualStatus: character.getVisualStatus?.() ?? null,
        })
      }
    }
  }

  /** Shared app-level talk owner consumes the active Café's exact visible roster. */
  getAgentTalkRoster(): readonly RosterAgent[] {
    return this.config.id === 'cafe' && this.active ? this.roster : []
  }

  private updateAction(active: boolean): void {
    if (!this.actionPrompt) return
    const state = useArenaStore.getState()
    if (!active || !canUseCampusRoomAction(state)) {
      this.currentAction = null
      this.actionPrompt.style.display = 'none'
      return
    }
    const camera = this.camera.getPosition()
    const origin = this.root.getPosition()
    this.currentAction = campusRoomActionAt(
      this.config.id,
      camera.x - origin.x,
      camera.z - origin.z,
    )
    this.actionPrompt.style.display = this.currentAction ? 'block' : 'none'
    if (this.currentAction) {
      this.actionPrompt.innerHTML = `<span style="display:inline-grid;place-items:center;min-width:20px;height:20px;margin-right:7px;border:1px solid rgba(255,255,255,.35);border-radius:5px">E</span>${this.currentAction.label}`
    }
  }

  destroy(): void {
    this.disposeInput?.()
    this.disposeInput = undefined
    this.unsubscribe?.()
    this.unsubscribe = undefined
    this.overlay?.dispose()
    this.overlay = null
    for (const texture of this.exchangeStatusTextures) texture?.destroy()
    this.exchangeStatusTextures = []
    this.exchangeStatusEntities = []
    this.exchangeStatusMaterials = []
    this.exchangeRunSignals = []
    this.exchangeCapacitySignals = []
    this.exchangeLastGood = undefined
    this.exchangeSemanticKey = ''
    this.exchangeMaterials = null
    this.workshopStatusTexture?.destroy()
    this.workshopStatusTexture = null
    this.workshopStatusEntity = null
    this.workshopStatusMaterial = null
    this.workshopBuildSignals = []
    this.workshopPluginSlots = []
    this.workshopLastGood = undefined
    this.workshopSemanticKey = ''
    this.workshopMaterials = null
    for (const texture of this.cafeStatusTextures) texture?.destroy()
    this.cafeStatusTextures = []
    this.cafeStatusEntities = []
    this.cafeStatusMaterials = []
    this.cafePresenceSignals = []
    this.cafeLastGood = undefined
    this.cafeSemanticKey = ''
    this.cafeAgentKey = ''
    for (const character of this.cafeCharacters.values()) character.destroy()
    this.cafeCharacters.clear()
    this.cafeMaterials = null
    for (const texture of this.datacentreStatusTextures) texture?.destroy()
    this.datacentreStatusTextures = []
    this.datacentreStatusEntities = []
    this.datacentreStatusMaterials = []
    this.datacentreServiceSignals = []
    this.datacentreWorldCore = null
    this.datacentreLastGood = undefined
    this.datacentreSemanticKey = ''
    this.datacentreMaterials = null
    this.roster = []
  }
}
